import { mockAccounts } from '@/mocks/accounts';
import { systemSettings } from '@/mocks/settings';
import type { Account, RegisterPayload } from '@/types';
import { delay } from './simulation';

/** Demo verification code. Never rendered in the UI. */
export const DEMO_OTP = '123456';

const otpIssueTimes: Record<string, number> = {};

export type LoginResult =
  | { ok: true; account: Account }
  | { ok: false; code: 'invalid' | 'pending' | 'rejected'; reason?: string };

export async function login(_email: string, _password: string): Promise<LoginResult> {
  await delay();
  // Demo mode: any credentials sign in with the approved demo account so the
  // dashboard can be reviewed freely without entering real credentials.
  // (Pending / rejected states are intentionally ignored on this path.)
  const account = mockAccounts.find((a) => a.status === 'approved');
  if (!account) return { ok: false, code: 'invalid' };
  return { ok: true, account };
}

export async function requestPasswordReset(email: string): Promise<{ ok: boolean }> {
  await delay(500);
  const normalized = email.trim().toLowerCase();
  otpIssueTimes[normalized] = Date.now();
  // Demo only: the code is logged for developers, never shown in the UI.
  if (import.meta.env.DEV) {
    console.info(`[LaundryBox demo] OTP for ${normalized}: ${DEMO_OTP}`);
  }
  return { ok: true };
}

export type VerifyResult = { ok: true } | { ok: false; error: 'invalid' | 'expired' };

export async function verifyResetCode(email: string, code: string): Promise<VerifyResult> {
  await delay(450);
  const normalized = email.trim().toLowerCase();
  const issuedAt = otpIssueTimes[normalized];
  if (!issuedAt) return { ok: false, error: 'invalid' };

  const ageMinutes = (Date.now() - issuedAt) / 60000;
  if (ageMinutes > systemSettings.otpValidityMinutes) {
    return { ok: false, error: 'expired' };
  }
  if (code.trim() !== DEMO_OTP) {
    return { ok: false, error: 'invalid' };
  }
  return { ok: true };
}

export async function resetPassword(
  email: string,
  code: string,
  _newPassword: string,
): Promise<VerifyResult> {
  const verification = await verifyResetCode(email, code);
  if (!verification.ok) return verification;
  await delay(450);
  // Demo simulation: no real credential is changed.
  return { ok: true };
}

export async function registerLaundry(
  _payload: RegisterPayload,
): Promise<{ ok: true; reference: string }> {
  await delay(900);
  // Demo simulation: no real account or document is created.
  const reference = `LB-REG-${Date.now().toString().slice(-6)}`;
  return { ok: true, reference };
}